import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Stack, Typography, Paper, Box, Alert, Chip, TextField, IconButton, CircularProgress, LinearProgress,
    Table, TableBody, TableCell, TableHead, TableRow, Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import NavBarBottom from "../../components/NavBarBottom";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import LoadingComponent from "../../components/LoadingComponent";
import SamplePickPlot from "../../components/SamplePickPlot";
import FinalizeDialog from "../../components/FinalizeDialog";
import { rgbString } from "../../helper/colorConversion";
import { IMAGE_SELECTION, selectionSidebar } from "../../helper/selectionFlow";
import { cardSx, groupWidthsLabel, useImageSelection } from "./ColorSelectionWidthGroups";

const BLACK_WHITE_AXES = { x: "C*", y: "speed / (lines × passes × width)", z: "L*" };
const MIN_CHROMATIC = 3;

/** Point of a sample for SamplePickPlot. */
function samplePoint(sample, coordinates) {
    return {
        id: sample.sample_id,
        ...coordinates,
        rgb: sample.rgb,
        lab: sample.lab,
        label: sample.sample_id.slice(0, 8),
        sublabel: sample.exploration_name,
        details: [
            ["Width", sample.width_mm ? `${(sample.width_mm * 1000).toFixed(1)} µm` : "—"],
            ["C*", sample.chroma.toFixed(1)],
            ["Speed metric", sample.speed_metric != null ? sample.speed_metric.toFixed(0) : "—"],
        ],
    };
}

/** Swatch + label of one sample. */
export function PickChip({ sample, label, onRemove }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, pl: 1, pr: onRemove ? 0 : 1, py: 0.5, width: "fit-content" }}>
            <Box sx={{ width: 20, height: 20, borderRadius: 0.75, bgcolor: rgbString(sample.rgb), border: "1px solid rgba(15,23,42,0.25)" }} />
            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                {`${label} · L* ${sample.lab[0].toFixed(1)} · ${sample.width_mm ? (sample.width_mm * 1000).toFixed(0) : "—"} µm`}
            </Typography>
            {onRemove && (
                <IconButton size="small" aria-label={`Remove ${sample.sample_id}`} onClick={onRemove}>
                    <CloseIcon fontSize="inherit" />
                </IconButton>
            )}
        </Stack>
    );
}

/** A number setting saved after a short pause once it is valid. Shows the
 *  saved value until it is edited; ``onPending`` is told while a valid
 *  change waits for its pause. */
function useSetting(value, isValid, save, onPending) {
    const [text, setText] = useState(null);
    const timer = useRef(null);
    useEffect(() => { setText(null); }, [value]);
    const shown = text ?? (value == null ? "" : String(value));
    const change = (next) => {
        setText(next);
        clearTimeout(timer.current);
        const number = Number(next);
        if (String(next).trim() === "" || !isValid(number)) {
            onPending(false);
            return;
        }
        onPending(true);
        timer.current = setTimeout(() => { save(number); onPending(false); }, 400);
    };
    return [shown, change, shown.trim() !== "" && isValid(Number(shown))];
}

/** Shown next to a setting while its result is computed. */
function ComputingNote({ show }) {
    if (!show) return null;
    return (
        <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={16} thickness={5} aria-label="Computing" />
            <Typography variant="caption" color="text.secondary">Computing…</Typography>
        </Stack>
    );
}

const dimmedSx = (computing) => ({ opacity: computing ? 0.45 : 1, transition: "opacity 0.15s ease" });

// Image selection: in the fixed width group, the chromatic primaries are
// selected automatically - backward elimination on the CIE xy gamut area,
// the smallest set within the area-loss limit; a clicked row of the curve
// keeps that many instead until a limit changes - plus one white and one
// black (preselected, replaceable by hand). Next starts the predictability
// test.
export default function ColorSelectionPrimaries() {
    const navigate = useNavigate();
    const { loading, errorMessage, payload, post, selectionId, stepRoute } = useImageSelection(navigate);
    const [limit, setLimit] = useState("5");
    const [draft, setDraft] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState(null);
    const timer = useRef(null);
    // A single black/white click waits here for the second one; the saved
    // pair lives on the server. A ref, so no re-render resets it.
    const pendingRef = useRef([]);

    const group = payload?.groups[0] ?? null;
    const automatic = group?.automatic ?? null;
    useEffect(() => {
        // No width group chosen yet: that step comes first.
        if (payload && !group) navigate(stepRoute("widthgroups", IMAGE_SELECTION), { replace: true });
    }, [payload, group]);
    useEffect(() => { if (payload) setLimit(String(payload.max_achromatic_chromaticity)); }, [payload?.max_achromatic_chromaticity]);

    const limitValue = Number(limit);
    const limitValid = String(limit).trim() !== "" && Number.isFinite(limitValue) && limitValue >= 0;
    const extra = limitValid ? { maxAchromaticChromaticity: limitValue } : {};

    // What is being computed: a change waiting for its pause (scheduled) or
    // a request on its way (running), per card.
    const [scheduled, setScheduled] = useState({});
    const [running, setRunning] = useState({});
    const pendingFor = (key) => (flag) => setScheduled((prev) => (prev[key] === flag ? prev : { ...prev, [key]: flag }));
    const run = async (scope, url, body) => {
        setRunning((prev) => ({ ...prev, [scope]: (prev[scope] || 0) + 1 }));
        try {
            return await post(url, body);
        } finally {
            setRunning((prev) => ({ ...prev, [scope]: Math.max(0, (prev[scope] || 0) - 1) }));
        }
    };
    const computingAutomatic = Boolean(scheduled.loss || scheduled.count || running.automatic);
    // A row of the curve clicked: that many chromatic primaries, the limits
    // set aside; "auto" returns to the limits' pick.
    const keepCount = (count) => run("automatic", "api/selection/image/automatic", { count, ...extra });
    const computingBlackWhite = Boolean(scheduled.limit || running.blackWhite);

    const [lossText, changeLoss, lossValid] = useSetting(
        automatic ? Number((automatic.drop_limit * 100).toFixed(2)) : null,
        (n) => Number.isFinite(n) && n >= 0 && n < 100,
        (n) => run("automatic", "api/selection/image/automatic", { dropLimitPercent: n, ...extra }),
        pendingFor("loss"),
    );
    const [countText, changeCount, countValid] = useSetting(
        automatic ? automatic.max_chromatic : null,
        (n) => Number.isInteger(n) && n >= MIN_CHROMATIC,
        (n) => run("automatic", "api/selection/image/automatic", { maxChromatic: n, ...extra }),
        pendingFor("count"),
    );

    const savedPair = group ? [group.white, group.black].filter(Boolean) : [];
    const shownPair = savedPair.length === 2 ? savedPair : draft;

    const toggleBlackWhite = (id) => {
        const current = savedPair.length === 2 ? savedPair : pendingRef.current;
        const next = current.includes(id) ? current.filter((s) => s !== id) : [...current, id].slice(-2);
        if (next.length === 2) {
            pendingRef.current = [];
            setDraft([]);
            run("blackWhite", "api/selection/image/picks", { group: group.key, role: "black_white", sampleIds: next, ...extra });
            return;
        }
        pendingRef.current = next;
        setDraft(next);
    };

    const changeLimit = (value) => {
        setLimit(value);
        clearTimeout(timer.current);
        const number = Number(value);
        if (String(value).trim() === "" || !Number.isFinite(number) || number < 0) {
            pendingFor("limit")(false);
            return;
        }
        pendingFor("limit")(true);
        timer.current = setTimeout(() => {
            run("blackWhite", "api/selection/image/groups", { maxAchromaticChromaticity: number });
            pendingFor("limit")(false);
        }, 400);
    };

    const confirm = async () => {
        setConfirming(true);
        setConfirmError(null);
        try {
            await axios.post("api/selection/image/primaries/confirm", { selectionId }, { timeout: 120000 });
            setConfirming(false);
            setConfirmOpen(false);
            navigate(stepRoute("predictability", IMAGE_SELECTION));
        } catch (error) {
            console.error("Error starting the predictability test:", error);
            setConfirming(false);
            setConfirmOpen(false);
            setConfirmError(error?.response?.data?.error || "Starting the predictability test failed.");
        }
    };

    const samples = payload?.samples || {};
    const ready = Boolean(group && group.chromatic.length > 0 && group.white && group.black);
    const computing = computingAutomatic || computingBlackWhite;
    const candidates = automatic?.curve?.[0]?.count ?? 0;

    return (
        <>
            <Box sx={{ width: "100%", maxWidth: "100%", mt: "2%", mb: 4, boxSizing: "border-box" }}>
                <ExplorationPageShell
                    title="Primaries"
                    subtitle="The chromatic primaries are selected automatically on the CIE xy gamut area, with one white and one black."
                    noPaper
                    stepSidebar={selectionSidebar("primaries", payload ? payload.totals.primaries : null, IMAGE_SELECTION, payload?.iterations)}
                    contentBoxSx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", px: 0, py: { xs: 2, md: 3 } }}
                >
                    <Stack spacing={3} sx={{ width: "100%", minWidth: 0, mt: 1 }}>
                        {(errorMessage || confirmError) && <Alert severity="error" sx={{ borderRadius: 2 }}>{errorMessage || confirmError}</Alert>}
                        {loading ? <LoadingComponent /> : payload && group && automatic && (
                            <>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 1 }}>
                                        {`${group.key} µm group · ${groupWidthsLabel(group)}`}
                                    </Typography>
                                    <Chip size="small" variant="outlined" label={`Iteration ${payload.iterations?.current ?? 1}`} />
                                    <Chip size="small" color="primary" label={`${group.primary_count} primaries`} />
                                    <Chip size="small" variant="outlined" label={`${group.cost.patches} predictability patches in ${group.cost.sets} sets`} />
                                    <Chip size="small" variant="outlined" label={`${(group.cost.area_mm2 / 100).toFixed(1)} cm²`} />
                                </Stack>

                                <Paper elevation={0} sx={cardSx}>
                                    <Typography variant="subtitle1" fontWeight={700}>Chromatic primaries</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {`${candidates} chromatic samples in the group. Removed one by one, the least xy gamut area lost first; the smallest set within the limit is kept. Click a row to keep that many instead.`}
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ my: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
                                        <TextField
                                            id="max-area-loss" size="small" type="number" label="Max. area loss (%)"
                                            value={lossText} onChange={(e) => changeLoss(e.target.value)} error={!lossValid}
                                            inputProps={{ min: 0, max: 99, step: 1 }} sx={{ width: 190 }}
                                        />
                                        <TextField
                                            id="max-chromatic" size="small" type="number" label="Max. chromatic primaries"
                                            value={countText} onChange={(e) => changeCount(e.target.value)} error={!countValid}
                                            inputProps={{ min: MIN_CHROMATIC, step: 1 }} sx={{ width: 210 }}
                                        />
                                        <ComputingNote show={computingAutomatic} />
                                    </Stack>
                                    {computingAutomatic && <LinearProgress sx={{ maxWidth: 720, mb: 1 }} />}
                                    {group.chromatic.length === 0 ? (
                                        <Alert severity="info" sx={{ borderRadius: 2 }}>No chromatic sample in this group.</Alert>
                                    ) : (
                                        <Box sx={dimmedSx(computingAutomatic)} aria-busy={computingAutomatic}>
                                            <Box sx={{ width: "100%", maxWidth: 720, overflowX: "auto" }}>
                                                <Table size="small" aria-label="Automatic reduction">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Chromatic primaries</TableCell>
                                                            <TableCell align="right">xy gamut area</TableCell>
                                                            <TableCell align="right">Area loss</TableCell>
                                                            <TableCell>Removed</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {automatic.curve.map((point) => {
                                                            const chosen = point.count === automatic.chosen_count;
                                                            const removed = point.removed ? samples[point.removed] : null;
                                                            const keep = () => { if (!chosen && !computingAutomatic) keepCount(point.count); };
                                                            return (
                                                                <TableRow
                                                                    key={point.count} selected={chosen} hover tabIndex={0}
                                                                    aria-label={`Keep ${point.count} chromatic primaries`}
                                                                    onClick={keep}
                                                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); keep(); } }}
                                                                    sx={{ cursor: chosen ? "default" : "pointer" }}
                                                                >
                                                                    <TableCell>
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            <span>{point.count}</span>
                                                                            {chosen && <Chip size="small" color="primary" label={automatic.manual ? "Selected by hand" : "Selected"} />}
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{Number(point.area).toFixed(4)}</TableCell>
                                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{`${(Number(point.loss) * 100).toFixed(1)} %`}</TableCell>
                                                                    <TableCell>
                                                                        {removed ? (
                                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: rgbString(removed.rgb), border: "1px solid rgba(15,23,42,0.25)" }} />
                                                                                <Typography variant="caption">{removed.sample_id.slice(0, 8)}</Typography>
                                                                            </Stack>
                                                                        ) : "—"}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                            {automatic.capped && (
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                                    Limited by the maximum number of chromatic primaries.
                                                </Typography>
                                            )}
                                            {automatic.manual && (
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Number of chromatic primaries chosen by hand; the limits are set aside.
                                                    </Typography>
                                                    <Button size="small" variant="text" onClick={() => keepCount("auto")} disabled={computingAutomatic} sx={{ py: 0 }}>
                                                        Automatic
                                                    </Button>
                                                </Stack>
                                            )}
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                                {group.chromatic.map((id, i) => samples[id] && (
                                                    <PickChip key={id} sample={samples[id]} label={`${i + 1}`} />
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
                                </Paper>

                                <Paper elevation={0} sx={cardSx}>
                                    <Typography variant="subtitle1" fontWeight={700}>White and black</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Samples of the explorations in this group's widths. Click two to replace the pair: the lighter is the white.
                                    </Typography>
                                    <Stack direction="row" spacing={2} sx={{ my: 1.5 }} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <TextField
                                            id="max-chromaticity" size="small" type="number" label="Max. chromaticity (C*)" value={limit}
                                            onChange={(e) => changeLimit(e.target.value)}
                                            error={!limitValid}
                                            inputProps={{ min: 0, step: 0.5 }}
                                            sx={{ width: 190 }}
                                        />
                                        <ComputingNote show={computingBlackWhite} />
                                    </Stack>
                                    {computingBlackWhite && <LinearProgress sx={{ maxWidth: 900, mb: 1 }} />}
                                    {group.black_white_candidates.length === 0 ? (
                                        <Alert severity="info" sx={{ borderRadius: 2 }}>No sample of these widths is within the chromaticity limit.</Alert>
                                    ) : (
                                        <Box aria-busy={computingBlackWhite} sx={{ width: "100%", maxWidth: 900, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", ...dimmedSx(computingBlackWhite) }}>
                                            <SamplePickPlot
                                                points={group.black_white_candidates.filter((id) => samples[id]?.speed_metric != null).map((id) => samplePoint(
                                                    samples[id], { x: samples[id].chroma, y: samples[id].speed_metric, z: samples[id].lab[0] }))}
                                                selectedIds={shownPair}
                                                onToggle={toggleBlackWhite}
                                                axes={BLACK_WHITE_AXES}
                                                height={440}
                                            />
                                        </Box>
                                    )}
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                                        {group.white && samples[group.white] && <PickChip sample={samples[group.white]} label="White" />}
                                        {group.black && samples[group.black] && <PickChip sample={samples[group.black]} label="Black" />}
                                    </Stack>
                                </Paper>

                                <Typography variant="body2" color="text.secondary">
                                    {`${group.primary_count} primaries: ${group.cost.pairs} pairs, ${group.cost.patches} predictability patches.`}
                                </Typography>
                            </>
                        )}
                        <NavBarBottom
                            variant="embedded"
                            returnLabel="Back"
                            prevPage={stepRoute("widthgroups", IMAGE_SELECTION)}
                            nextPage={stepRoute("predictability", IMAGE_SELECTION)}
                            enableNextPage={!ready || computing}
                            nextLoading={confirming}
                            nextPageClickFunc={() => setConfirmOpen(true)}
                        />
                    </Stack>
                </ExplorationPageShell>
            </Box>
            <FinalizeDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirm}
                confirming={confirming}
                title="Start the predictability test?"
                text={group ? `${group.primary_count} primaries, ${group.cost.pairs} pairs, ${group.cost.patches} patches in ${group.cost.sets} sets. The width group and these settings are then fixed.` : ""}
            />
        </>
    );
}
